import * as Yup from "yup";
import moment from "moment";
import { TARGETING_FIELDS } from "./adManagementFormConfig";

/* Maximum allowed scheduling window. */
const MAX_RANGE_DAYS = 90;

/* Start of today (local time) — used so past dates are rejected but
 * today itself is still allowed. */
const startOfToday = () => moment().startOf("day").toDate();

/* Human-readable device labels used in error messages. */
const DEVICE_LABELS = {
  mobile: "Mobile",
  tablet: "Tablet",
  windows: "Desktop",
};

/* A targeting field counts as "filled" when its value is a non-empty
 * array (multiselect / checkbox) or a truthy string/number (text / number). */
const hasTargetingValue = (val) => {
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === "string") return val.trim().length > 0;
  return Boolean(val);
};

const toCurrencyNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const amount = Number(String(value).replaceAll(",", "").trim());
  return Number.isFinite(amount) ? amount : null;
};

const buildBaseAdSchemaFields = (
  estimatedCost = 0,
  validateDiscountedAmount = true,
) => ({
  bannerName: Yup.string()
    .trim()
    .required("Name is required")
    .max(50, "Banner Name cannot exceed 50 characters"),
  position: Yup.string().required("Position is required"),
  targetDevices: Yup.array().min(
    1,
    "Select all devices this ad should target.",
  ),
  ContentRedirectType: Yup.string().required("Content Redirect is required"),
  /* When ContentRedirectType === "external_web", externalUrl is required and
   * must be a valid URL. Other redirect types skip this rule. */
  externalUrl: Yup.string().when(
    "ContentRedirectType",
    (contentRedirectType, schema) =>
      contentRedirectType === "external_web"
        ? schema
            .trim()
            .required("External URL is required")
            .url("Enter a valid URL (e.g. https://example.com)")
        : schema.notRequired(),
  ),
  /* When ContentRedirectType === "specific_content", at least one linked
   * content / catalog / bundle must be selected. */
  linkedContentIds: Yup.array().when(
    "ContentRedirectType",
    (contentRedirectType, schema) =>
      contentRedirectType === "specific_content"
        ? schema.min(1, "Select at least one content, catalog, or bundle.")
        : schema.notRequired(),
  ),
  /* When "Show to all Subscribers" is unchecked, the user must fill in at
   * least one targeting field. Reads the sibling `showToAll` flag from the
   * parent (Formik root values). */
  targeting: Yup.object().test(
    "at-least-one-targeting",
    'Select at least one targeting field, or enable "Show to all Subscribers".',
    function validateTargeting(value) {
      const parent = this.parent || {};
      if (parent.showToAll) return true;
      const filled = TARGETING_FIELDS.some((f) =>
        hasTargetingValue(value?.[f.key]),
      );
      return filled;
    },
  ),
  /* Every selected device must have an uploaded image (a freshly-picked file
   * OR a preview URL hydrated from a previously-saved record). Emits one
   * error per missing device at path `deviceFiles.<device>` so the inline
   * message renders directly under the corresponding upload field. */
  deviceFiles: Yup.object().test(
    "device-image-required",
    "Upload an image for every selected device.",
    function validateDeviceImages(value) {
      const parent = this.parent || {};
      const devices = Array.isArray(parent.targetDevices)
        ? parent.targetDevices
        : [];
      const previews = parent.devicePreviewUrls || {};
      const missing = devices.filter((d) => !value?.[d] && !previews?.[d]);
      if (missing.length > 0) {
        const innerErrors = missing.map((d) =>
          this.createError({
            path: `deviceFiles.${d}`,
            message: `Upload an image for ${DEVICE_LABELS[d] ?? d}.`,
          }),
        );
        throw new Yup.ValidationError(innerErrors);
      }
      return true;
    },
  ),
  discountedAmount: Yup.mixed().test(
    "discount-not-greater-than-estimated",
    "Discounted Price cannot be greater than Estimated Cost",
    (value) => {
      if (!validateDiscountedAmount) return true;
      const discountedAmount = toCurrencyNumber(value);
      if (discountedAmount === null || discountedAmount <= 0) return true;
      const cost = toCurrencyNumber(estimatedCost) ?? 0;
      return discountedAmount <= cost;
    },
  ),
});

/* Schema for Add/Edit Ad form. Field names must match Formik values keys
 * in `AddEditAdManagement.js` exactly. */
export const buildAdManagementSchema = (
  estimatedCost = 0,
  validateDiscountedAmount = true,
) =>
  Yup.object().shape({
    ...buildBaseAdSchemaFields(estimatedCost, validateDiscountedAmount),
    startDate: Yup.date()
      .nullable()
      .required("Start Date is required")
      .typeError("Invalid date")
      .min(startOfToday(), "Start Date cannot be in the past"),
    endDate: Yup.date()
      .nullable()
      .required("End Date is required")
      .typeError("Invalid date")
      .min(Yup.ref("startDate"), "End Date must be after Start Date")
      .test(
        "max-90-days",
        `End Date must be within ${MAX_RANGE_DAYS} days of Start Date`,
        function validateRange(value) {
          const { startDate } = this.parent || {};
          if (!startDate || !value) return true;
          const maxEnd = moment(startDate)
            .add(MAX_RANGE_DAYS, "days")
            .endOf("day");
          return moment(value).isSameOrBefore(maxEnd);
        },
      ),
  });

export const AdManagementSchema = buildAdManagementSchema();

export const buildEditAdManagementSchema = (
  estimatedCost = 0,
  validateDiscountedAmount = true,
) =>
  Yup.object().shape(
    buildBaseAdSchemaFields(estimatedCost, validateDiscountedAmount),
  );

/* Edit mode bypasses date validation while keeping all other field rules. */
export const EditAdManagementSchema = buildEditAdManagementSchema();

/* View mode bypasses validation entirely. */
export const ViewAdManagementSchema = Yup.object();
